import { AppButtonType } from 'utils/webviewHelpers';
import { TPlanList } from 'typings';

// supported payment method types
export const PAYMENT_METHOD_TYPE = {
  CARD: 'card',
  CASHAPP: 'cashapp'
} as const;

// Which failure copy the shared popup shows.
export type FailureKind = 'payment' | 'addCard';

export type StripeActionResult = { ok: boolean; message?: string };

// What each host does once Stripe hands back a payment method. `run` owns the
// host's business call; return { ok: false } (or throw) to keep the user on the
// form with the failure popup.
export interface StripeActionHandler {
  readonly failureKind: FailureKind;
  run(paymentMethodId: string): Promise<StripeActionResult>;
}

export type TPaymentMethodType =
  (typeof PAYMENT_METHOD_TYPE)[keyof typeof PAYMENT_METHOD_TYPE];

// Pascal case of PAYMENT_METHOD_TYPE, e.g. 'Card' | 'Cashapp'. Used for i18n keys.
export type TPaymentMethodTypePascal = Capitalize<TPaymentMethodType>;

export type TPaymentMethodTypeValue =
  TPaymentMethodType | (string & Record<never, never>);

export type TPaymentMethodsList = {
  default_payment_method: string;
  // Spec ("API implementations for Invoice and Payment method page" → List
  // Payment Methods) returns `payment_method`
  payment_method: TPaymentMethods[];
};

export type TPaymentMethods = {
  id: string;
  type: TPaymentMethodTypeValue;
  // Absent for non-card methods — Cash App carries `cashapp_details` instead.
  card?: TCardInfo;
  /** Present when `type === 'cashapp'`. */
  cashapp_details?: TCashAppDetails;
};

export type TCard = {
  // Legacy flat `card_details` carries the saved payment-method id; absent on
  // the spec `payment_method_details.card_details` shape.
  id?: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
};

// card shape from /piggybank/payment_gateway/payment_methods (includes funding)
export type TCardInfo = {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  funding: string;
};

export type TCashAppDetails = {
  buyer_id: string;
  cashtag: string;
};

/**
 * Spec wraps card/cashapp details under `payment_method_details` on a purchase.
 * Older responses expose `card_details` directly on the subscription; readers
 * should prefer `payment_method_details.id` and fall back to `card_details.id`.
 */
export type TPaymentMethodDetails = {
  id: string;
  type: TPaymentMethodTypeValue;
  card_details: TCard;
  cashapp_details?: TCashAppDetails;
};

/**
 * A subscription's current payment method, flattened so the listing screens can
 * match it against the saved-methods list (by id, or by legacy card fields). A
 * card carries the TCard fields; Cash App carries `cashapp_details` and no card
 * fields. Sourced in the subscription reducer from `payment_method_details`,
 * falling back to the legacy flat `card_details`.
 */
export type TDefaultPaymentMethod = Partial<TCard> & {
  id?: string;
  type?: TPaymentMethodTypeValue;
  cashapp_details?: TCashAppDetails;
};

export type TPrimePaymentMethodsState = {
  allPaymentMethods: TPrimePaymentMethod[];
  isLoading: boolean;
};

export type TPaymentMethodsState = {
  allPaymentMethods: TPaymentMethods[];
  isLoading: boolean;
};

export const PRICE_TYPE = {
  RECURRING: 'Recurring',
  ONE_TIME: 'OneTime'
} as const;

export const PURCHASE_TYPE = {
  RECURRING: 'Recurring',
  MANUAL_TOP_UP: 'ManualTopUp',
  AUTO_TOP_UP: 'AutoTopUp'
} as const;

export type TPriceType = (typeof PRICE_TYPE)[keyof typeof PRICE_TYPE];

export type TPurchasePayload = {
  country: string;
  sku_id: string;
  trial: boolean;
  space_id: string | null | undefined;
  price_type?: TPriceType;
  action?: string; // will be deprecated
  previous_purchase_id?: string; // will be deprecated
};

export type TCheckoutSessionPayload = {
  country: string;
  sku_id: string;
  space_id: string;
};

export type TProps_PaymentOptions = {
  planSelected?: TPlanList;
  purchaseId?: string;
  deviceId?: string;
  toggleModal?: () => void;
  isFreeTrial?: boolean;
  isUpgrade?: boolean;
  previousSubscriptionSKU?: string;
};

export type TProps_PaymentScreen = {
  // What runs on a confirmed payment method + which failure popup to show.
  action: StripeActionHandler;
  amount: number;
  isFreeTrial: boolean;
  isUpgrade: boolean;
  isCancellationAndRefundPolicyAccepted?: boolean;
  planSelected?: TPlanList;
  isPreviewPlan?: boolean;
  isDowngrade?: boolean;
  currency: string;
  errorMessageProrate: string;
  ctaLabel?: string;
  /** Fires when Stripe Payment Element has finished loading. */
  onReady?: () => void;
  /** Fires when the user picks a different method type inside the Stripe Payment Element */
  onPaymentMethodTypeChange?: (type: string) => void;
  /** Keeps the CTA busy while the host's post-confirm request runs. */
  isSubmitting?: boolean;
  /** App-buttons restored after the form hides them on submit. Defaults to [RELOAD, BACK]. */
  idleAppButtons?: AppButtonType[];
};

export type TUpgradPayload = {
  purchase_id: string;
  stripe_payment_method: string;
};

/**
 * Create Subscription body (Confluence "API implementations for Invoice and
 * Payment method page"). `purchase_id` comes from Create Initial Purchase;
 * `coupon`/`trial_days` are optional.
 */
export type TCreateSubscriptionPayload = {
  purchase_id: string;
  stripe_payment_method: string;
  coupon?: string;
  trial_days?: number;
};

export type TDowngradePayload = {
  sku_id: string;
  stripe_payment_method: string;
};

export type TPrimeBuySubscriptionPayload = {
  session_id: string;
  free_trial: boolean;
  payment_method_id: string;
};

export type TSetupIntent = {
  client_secret: string;
};

export type TChangePaymentMethod = {
  stripe_payment_method: string;
};

export type TChangePaymentMethodV2 = {
  payment_method_id: string;
};

export type TChangePaymentMethodRes = {
  client_secret: string;
  id: string;
  status: string;
};

export type TChangePaymentMethodResV2 = {
  subscription_id: string;
};

/**
 * FCM-2514 view-model the /retryPayment screen renders. Built from a
 * subscription object (subscriptions endpoint filtered by `_id`), which carries
 * card_details, next_bill_amount, next_billing_date, currency and pay_status.
 * See helpers.ts#bannerFromSubscription.
 */
export type TFailedPaymentBanner = {
  subscription_id: string;
  plan_name?: string;
  // subscription_cycle from the API: 1 = monthly, otherwise yearly.
  subscription_cycle?: number;
  currency: string;
  // next_bill_amount, in cents: 999 means $9.99.
  amount_due: number;
  // next_billing_date, epoch milliseconds.
  due_date?: number;
  payment_method_details?: TPaymentMethodDetails;
  // subscription pay_status, e.g. 'Success' | 'Failed'.
  status?: string;
  // Days remaining in the grace window before the subscription lapses. Source
  // is the failed-payment banner API (see helpers.ts) — omitted when unknown.
  grace_period_days?: number;
};

/**
 * FCM-2514 response of the POST /retry placeholder endpoint. A resolved 2xx is
 * treated as success, a rejected request as failure. Update once the real
 * contract lands.
 */
export type TRetryPaymentRes = {
  // e.g. 'success' | 'failed' | 'processing'
  status?: string;
  subscription_id?: string;
  message?: string;
  // Decline reason surfaced in the failure popup ("Your card declined due to
  // {reason_for_failure}."). Field name to be confirmed against the real
  // contract; falls back to `message` when absent.
  reason_for_failure?: string;
};

export type TProrateChargesPayload = {
  sku_id: string;
};

export type TPrimePaymentMethod = {
  brand: string;
  customer_id: string;
  expiry_month: number;
  expiry_year: number;
  id: string;
  last_4_digits: string;
  type: string;
};

export type TPrimePaymentMethodList = {
  payment_methods: TPrimePaymentMethod[];
};
